import { useStyletron } from "baseui";
import type { Theme, Typography } from "baseui/theme";
import type { ReactHTML } from "react";
import * as React from "react";
import { Waypoint } from "react-waypoint";
import type { StyleObject } from "styletron-react";
import { parameterize } from "superflected";
import { Anchor } from "web/src/components/edit/shared/Anchor";
import { useHover } from "web/src/lib/hooks";
import { getChildrenText } from "web/src/lib/utils";
import { useDocsLocation } from "../chrome/nav/useDocsLocation";
import { crawlingHintRegex } from "../utils/extractMDXHeadings";

const removeStringFromNode = (node: React.ReactNode, pattern: RegExp): React.ReactNode => {
  if (!node) return node;

  if (typeof node === "string") {
    return node.replace(pattern, "");
  }

  if (Array.isArray(node)) {
    return node.map((child) => removeStringFromNode(child, pattern));
  }

  if (React.isValidElement(node)) {
    if (node.props.children) {
      return React.cloneElement(node, {
        ...node.props,
        children: removeStringFromNode(node.props.children, pattern),
      });
    }
    return node;
  }

  return node;
};

export const Heading = ({
  element,
  fontType,
  children,
  $style,
  id,
}: {
  element: keyof ReactHTML;
  fontType: keyof Theme["typography"];
  children: React.ReactNode;
  $style?: StyleObject;
  id?: string;
}) => {
  const [isHovered, hoverProps] = useHover();
  const text = getChildrenText(children);
  const slug = id ?? parameterize(text.replace(crawlingHintRegex, ""));
  const [css, $theme] = useStyletron();
  const [location, setLocation] = useDocsLocation();
  const currentPage = location.replace(/#.*$/, "");
  const childCount = React.Children.count(children);
  const crawlingHints = text.match(crawlingHintRegex)?.[1].split(",");
  const sanitizedChildren = removeStringFromNode(children, crawlingHintRegex);

  const itemId = `${currentPage}#${slug}`;

  return React.createElement(
    element,
    {
      ...hoverProps,
      id: slug,
      "data-docscategory": true,
      "data-ch": JSON.stringify(crawlingHints),
      className:
        css({
          ...$theme.typography[fontType],
          color: $theme.colors.contentPrimary,
          display: "flex",
          alignItems: "center",
          gap: $theme.sizing.scale400,
          marginTop: $theme.sizing.scale900,
          marginBottom: $theme.sizing.scale700,
          flexWrap: "wrap",
          scrollMarginTop: $theme.sizing.scale1600,
          ...$style,
        }) + " docs-heading",
    },
    <React.Fragment>
      {React.Children.map(sanitizedChildren, (child, idx) => {
        // For the headings that wrap on smaller screens we need to make sure that that anchor does not wrap on its own to a next line so we add it to the last child with a non-break space
        if (idx === childCount - 1) {
          return (
            <span>
              {child}&nbsp;
              {element !== "h5" && element !== "h6" && <Anchor isVisible={isHovered} slug={slug} element={element} />}
            </span>
          );
        }

        return child;
      })}
      {typeof window != "undefined" && element !== "h5" && element !== "h6" && (
        <Waypoint
          scrollableAncestor={window}
          onEnter={({ event }) => {
            const eventTargetLocation = (event?.target as Document)?.location;
            try {
              const currentPageUrl = new URL("http://example.com" + currentPage);
              if (eventTargetLocation?.pathname === currentPageUrl.pathname) {
                setLocation(itemId, { replace: true });
              }
            } catch (e) {
              console.debug("Error parsing current page URL", e);
            }
          }}
          bottomOffset={"90%"}
          fireOnRapidScroll={false}
        />
      )}
    </React.Fragment>
  );
};

export const headingComponent = (element: keyof ReactHTML, fontType: keyof Typography, $style?: StyleObject) => {
  const HeadingComponent = (props: { children: React.ReactNode; id?: string }) => {
    return <Heading element={element} fontType={fontType} {...props} $style={$style} />;
  };
  return HeadingComponent;
};

export const H1 = headingComponent("h1", "HeadingLarge");
export const H2 = headingComponent("h2", "HeadingMedium");
export const H3 = headingComponent("h3", "HeadingSmall");
export const H4 = headingComponent("h4", "HeadingXSmall");
export const H5 = headingComponent("h5", "HeadingXSmall");
export const H6 = headingComponent("h6", "HeadingXSmall");
